import React, { useState, useMemo, useRef, useEffect } from 'react';
import './Tasks.css';
import { useCache } from '../../hooks/useCache';
import { API_ENDPOINTS } from '../../config/api';
import { cacheGet, cacheSet, emitCacheUpdated } from '../../utils/cache';

// Redéploiement Vercel

const DAY_NAMES = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const MON_FIRST = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'];
const MONTH_ABBR = ['Jan','Fév','Mar','Avr','Mai','Jui','Jui','Aoû','Sep','Oct','Nov','Déc'];

// ---- Cache keys & helpers (Tasks feed) ----
const KEY_LIST   = `tasks/list`;
const KEY_CURSOR = `tasks/cursor`;
const KEY_LAST   = `tasks/lastLoad`; // timestamp dernière tentative (anti-spam)

function mergeById(prev = [], next = []) {
  const map = new Map(prev.map(x => [x.id, x]));
  for (const it of next) if (!map.has(it.id)) map.set(it.id, it);
  return Array.from(map.values());
}

const truthy = v => v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';

const isIncidentMeta = (meta = {}) => {
  const s = String(meta.status ?? meta.state ?? '').toLowerCase();
  const sev = String(meta.severity ?? meta.level ?? '').toLowerCase();
  const tags = Array.isArray(meta.tags) ? meta.tags.map(x => String(x).toLowerCase()) : [];
  const flag = meta.incident ?? meta.is_incident ?? meta.has_incident ?? meta.warning ?? meta.alert ?? meta.late;

  return (
    s === 'incident' || s === 'warning' || s === 'alert' ||
    sev === 'incident' || sev === 'warning' ||
    truthy(flag) ||
    (!!meta.incident_reason || !!meta.reason) ||
    tags.includes('incident') || tags.includes('warning')
  );
};

const startOfDayChart = d => { const x=new Date(d); x.setHours(0,0,0,0); return x; };
const endOfDayChart   = d => { const x=new Date(d); x.setHours(23,59,59,999); return x; };

// ——— Dates → YMD ———
const ymd = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`;
};

// ——— Date d'évènement du log (prend "completed_at" si présent) ———
const pickLogDate = (log) => {
  const m = log?.meta || {};
  const cand = m.completed_at || m.done_at || m.timestamp || log.occurred_at || log.created_at;
  return cand ? new Date(cand) : null;
};

// ——— Compteurs sur un sous-ensemble de logs mappés ———
function countByStatus(logs = []) {
  let done = 0, incident = 0, missed = 0, failed = 0, pending = 0;
  for (const t of logs) {
    switch (t.statusType) {
      case 'done':     done++;     break;
      case 'incident': incident++; break;
      case 'missed':   missed++;   break;
      case 'failed':   failed++;   break;
      case 'pending':
      default: pending++; break;
    }
  }
  return { done, incident, missed, failed, pending };
}

function thisWeekBounds() {
  const now = new Date();
  const day = now.getDay(); // 0=Dim..6=Sam
  const diffToMon = (day === 0 ? -6 : 1 - day);
  const mon = startOfDayChart(new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMon));
  const sun = endOfDayChart(new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6));
  return [mon, sun];
}

const mondayIndex = d => (d.getDay() + 6) % 7; // Lundi=0..Dim=6

// Helpers pour les tâches du jour
const parseTime = (hhmm = '00:00') => {
  const [h, m] = String(hhmm || '00:00').split(':').map(n => parseInt(n, 10) || 0);
  return { h, m };
};

const durationMs = (row) => Math.max(0, Number(row.duration_minutes ?? 0)) * 60_000;

const buildWindow = (row) => {
  const [y, mo, da] = String(row.due_date).split('-').map(n => parseInt(n, 10));
  const { h, m } = parseTime(row.start_time);
  const start = new Date(y, (mo || 1) - 1, da || 1, h || 0, m || 0, 0, 0); // local time
  const end   = new Date(start.getTime() + durationMs(row));
  return { start, end };
};

const fmtDelta = (ms) => {
  const sign = ms < 0 ? -1 : 1;
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  if (h > 0) return `${sign < 0 ? '-' : ''}${h}h${String(m).padStart(2,'0')}`;
  return `${sign < 0 ? '-' : ''}${m} min`;
};

const computeTodayStatus = (row) => {
  const now = new Date();
  const { start, end } = buildWindow(row);
  const hasDuration = durationMs(row) > 0;

  if (!hasDuration) {
    if (now < start) return { label:'Pas commencé', tone:'idle' };
    return { label:'En retard', tone:'late' };
  }
  if (now < start) return { label:`Pas commencé (début dans ${fmtDelta(start - now)})`, tone:'idle' };
  if (now <= end)  return { label:`Reste ${fmtDelta(end - now)}`, tone:'ongoing' };
  return { label:`En retard de ${fmtDelta(now - end)}`, tone:'late' };
};

const displayOperator = (row) =>
  (row.assigned_id == null) ? 'Tout le monde'
    : ((row.first_name && row.last_name) ? `${row.first_name} ${row.last_name}` : '—');

// Bornes de période
const startOfDay = d => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const endOfDay   = d => { const x = new Date(d); x.setHours(23,59,59,999); return x; };

/** KPIs & Opérateurs — suivent exactement le sélecteur */
function getKpiBounds(period) {
  const now = new Date();

  if (period === 'day') {
    return [startOfDay(now), endOfDay(now)]; // AUJOURD'HUI uniquement
  }

  if (period === 'week') {
    // Semaine DERNIÈRE (lundi→dimanche)
    const dow = now.getDay();                       // 0=Dim..6=Sam
    const diffToMon = (dow === 0 ? -6 : 1 - dow);
    const mondayThisWeek = startOfDay(new Date(
      now.getFullYear(), now.getMonth(), now.getDate() + diffToMon
    ));
    const mondayLastWeek = startOfDay(new Date(
      mondayThisWeek.getFullYear(), mondayThisWeek.getMonth(), mondayThisWeek.getDate() - 7
    ));
    const sundayLastWeek = endOfDay(new Date(
      mondayLastWeek.getFullYear(), mondayLastWeek.getMonth(), mondayLastWeek.getDate() + 6
    ));
    return [mondayLastWeek, sundayLastWeek];
  }

  if (period === 'month') {
    const first = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
    const last  = endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    return [first, last];
  }

  return [new Date(0), new Date(8640000000000000)];
}

/** Tableau — suit le sélecteur, MAIS:
 *  - pour 'day' => semaine en cours (lundi → aujourd'hui)
 */
function getTableBounds(period) {
  const now = new Date();

  if (period === 'day') {
    const dow = now.getDay();
    const diffToMon = (dow === 0 ? -6 : 1 - dow);
    const mondayThisWeek = startOfDay(new Date(
      now.getFullYear(), now.getMonth(), now.getDate() + diffToMon
    ));
    return [mondayThisWeek, endOfDay(now)]; // lundi → aujourd'hui
  }

  if (period === 'week') {
    // Semaine DERNIÈRE
    return getKpiBounds('week');
  }

  if (period === 'month') {
    // Mois courant
    return getKpiBounds('month');
  }

  return [new Date(0), new Date(8640000000000000)];
}

// Normalise les données weekly venant du backend (snake_case → camelCase + noms de jours)
function normalizeWeeklyTasks(weeklyRaw) {
  const byDow = new Map();
  (Array.isArray(weeklyRaw) ? weeklyRaw : []).forEach((r) => {
    const dow = Number(r.day_of_week ?? r.dow ?? r.dayIndex ?? r.day);
    if (!Number.isFinite(dow)) return;
    const total     = parseInt(r.total ?? r.total_tasks ?? 0, 10) || 0;
    const completed = parseInt(r.completed ?? r.completed_tasks ?? 0, 10) || 0;
    const late      = parseInt(r.late ?? r.late_tasks ?? 0, 10) || 0;
    const notDone   = parseInt(r.not_done ?? r.notDone ?? r.failed ?? 0, 10) || 0;
    byDow.set(dow, { total, completed, late, notDone });
  });

  // On renvoie toujours 7 jours dans l'ordre (0..6)
  return DAY_NAMES.map((name, idx) => {
    const v = byDow.get(idx) || { total: 0, completed: 0, late: 0, notDone: 0 };
    return { day: name, ...v };
  });
}

function Tasks({ onNavigate }) {
  // localStorage sécurisé
  const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
  
  const [selectedPeriod, setSelectedPeriod] = useState('day');
  const chartRef = useRef(null);
  
  // mémoriser si l'utilisateur a changé la période
  const userPickedPeriodRef = useRef(false);
  const [tt, setTt] = useState({ show:false, x:0, y:0, bucket:null });
  const [todayPending, setTodayPending] = useState([]);
  const [instancesCount, setInstancesCount] = useState(null);
  const uid = useMemo(() => Number(safeGet('userId') || 0), []);
  const token = useMemo(() => safeGet('token') || '', []);
  const [nowTick, setNowTick] = useState(Date.now());

  const showTT = (e, bucket) => {
    const rect = chartRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTt({
      show: true,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 12,
      bucket
    });
  };
  const moveTT = (e) => {
    if (!tt.show) return;
    const rect = chartRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTt(t => ({ ...t, x: e.clientX - rect.left, y: e.clientY - rect.top - 12 }));
  };
  const hideTT = () => setTt({ show:false, x:0, y:0, bucket:null });

  const { list, stats, weekly, opDay, opWeek, opMonth } = useCache({
    list:   'tasks/list',
    stats:  'tasks/stats',
    weekly: 'tasks/weekly',
    opDay:   'tasks/operators/day',
    opWeek:  'tasks/operators/week',
    opMonth: 'tasks/operators/month'
  });

  // Auto-load par curseur (au mount + si l'user scrolle en bas via bouton)
  useEffect(() => {
    let running = false;

    async function loadNextPage(reason = 'auto') {
      if (running) return;
      running = true;
      try {
        const uid = Number(localStorage.getItem('userId') || 0);
        if (!uid) return;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const cursorState = cacheGet(KEY_CURSOR) || { next: null };
        const lastTs = Number(cacheGet(KEY_LAST) || 0);
        // anti-boucle si on spam le bouton
        if (reason === 'auto' && Date.now() - lastTs < 1500) return;

        const url = API_ENDPOINTS.TASKS_FEED({
          user_id: uid,
          limit: 100,
          cursor: cursorState.next || undefined
        });

        const res = await fetch(url, { headers });
        if (!res.ok) return;
        const { items = [], next_cursor = null } = await res.json();

        const prev = cacheGet(KEY_LIST) || [];
        const merged = mergeById(prev, items);

        cacheSet(KEY_LIST, merged);
        cacheSet(KEY_CURSOR, { next: next_cursor, ts: Date.now() });
        cacheSet(KEY_LAST, Date.now());
        emitCacheUpdated(['tasks/list']);
      } catch(e) {
        console.warn('[Tasks] loadNextPage error', e);
      } finally {
        running = false;
      }
    }

    // 1) premier passage : si liste vide mais cursor pas null, on charge une page
    const initList = cacheGet(KEY_LIST) || [];
    const initCursor = cacheGet(KEY_CURSOR);
    if (initList.length === 0 || (initCursor && initCursor.next)) {
      loadNextPage('auto');
    }

    // 2) expose une fonction globale (optionnel) pour bouton "Charger plus"
    window.__tasksLoadMore = () => loadNextPage('manual');

    return () => { window.__tasksLoadMore = null; };
  }, [token]);

  // headers auth communs
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // 🔁 Fonction de mapping des statuts (4 statuts officiels)
  function mapStatus(meta = {}) {
    const s = String(meta.status ?? meta.state ?? '').toLowerCase();
    if (s === 'done' || s === 'completed')      return { type: 'done',     label: 'Terminée' };
    if (s === 'incident' || isIncidentMeta(meta)) return { type: 'incident', label: 'Incident' };
    if (s === 'missed')                          return { type: 'missed',  label: 'Non réalisé' };
    if (s === 'failed' || s === 'error' || truthy(meta.not_applicable) || truthy(meta.skipped))
                                                  return { type: 'failed',  label: 'Non réalisable' };
    return { type: 'pending', label: 'À faire' };
  }

  // ✅ tasksRaw doit être défini AVANT les useEffect qui le lisent
  const tasksRaw = useMemo(() => (Array.isArray(list) ? list : []).map(log => {
    const meta = log.meta || {};
    const { type, label } = mapStatus(meta);
    const createdAt = pickLogDate(log);
    return {
      id: log.id,
      meta,
      title: meta.task_title || log.description || 'Tâche sans titre',
      operator: (log.first_name && log.last_name) ? `${log.first_name} ${log.last_name}` : 'Non assigné',
      createdAt,
      dateStr: createdAt ? createdAt.toLocaleDateString('fr-FR') : '',
      statusType: type, // 'done' | 'incident' | 'missed' | 'failed' | 'pending'
      status: label,
    };
  }), [list]);

  // essaie de récupérer un nombre d'instances sur une plage (si endpoint existe), sinon null
  async function getInstancesCountSafe(uid, fromYMD, toYMD) {
    try {
      let url = null;
      if (typeof API_ENDPOINTS?.TASK_INSTANCES_COUNT === 'function') {
        url = API_ENDPOINTS.TASK_INSTANCES_COUNT(uid, fromYMD, toYMD);
      } else if (typeof API_ENDPOINTS?.TASK_INSTANCES_RANGE === 'function') {
        url = API_ENDPOINTS.TASK_INSTANCES_RANGE(uid, fromYMD, toYMD);
      }
      if (!url) return null;

      const res = await fetch(url, { headers: authHeaders });
      const data = await res.json();
      if (Array.isArray(data)) return data.length;
      if (data && typeof data.count === 'number') return data.count;
      if (data && Array.isArray(data.items)) return data.items.length;
      return null;
    } catch {
      return null;
    }
  }

  // Fetch des tâches du jour
  useEffect(() => {
    if (!uid) return;
    let abort = false;
    (async () => {
      try {
        const res = await fetch(API_ENDPOINTS.TASK_INSTANCES_TODAY_PENDING(uid), {
          headers: authHeaders
        });
        const data = await res.json();
        if (!abort) setTodayPending(Array.isArray(data) ? data : []);
      } catch {
        if (!abort) setTodayPending([]);
      }
    })();
    return () => { abort = true; };
  }, [uid, authHeaders]);

  // recharge le dénominateur "instances" quand la période change
  useEffect(() => {
    let cancel = false;
    if (!uid) return;

    (async () => {
      const [ks, ke] = getKpiBounds(selectedPeriod);
      const from = ymd(ks), to = ymd(ke);

      if (selectedPeriod === 'day') {
        let count = await getInstancesCountSafe(uid, from, to);

        if (count == null) {
          const todayLogs = (tasksRaw || []).filter(t => t.createdAt && t.createdAt >= ks && t.createdAt <= ke);
          const { done, incident, missed, failed } = countByStatus(todayLogs);
          // Fallback: pendingToday (task_instance) + issues des logs
          count = (todayPending?.length || 0) + done + incident + missed + failed;
        }
        if (!cancel) setInstancesCount(count);
        return;
      }

      const maybe = await getInstancesCountSafe(uid, from, to);
      if (!cancel) setInstancesCount(maybe);
    })();

    return () => { cancel = true; };
  }, [selectedPeriod, uid, authHeaders, tasksRaw, todayPending]);

  // Rafraîchissement auto du compte à rebours
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000); // toutes les minutes
    return () => clearInterval(id);
  }, []);

  // basculer automatiquement sur « week » si le Jour est vide
  useEffect(() => {
    // ne jamais écraser un choix manuel
    if (userPickedPeriodRef.current) return;
    if (selectedPeriod !== 'day') return;

    // borne "aujourd'hui"
    const [ks, ke] = getKpiBounds('day');

    // logs du jour
    const dayLogs = (tasksRaw || []).filter(
      t => t.createdAt && t.createdAt >= ks && t.createdAt <= ke
    );
    const { done, incident, missed, failed } = countByStatus(dayLogs);

    // même logique que ton KPI "Nombre de tâches" (jour)
    const dayTotal =
      (todayPending?.length || 0) + done + incident + missed + failed;

    if (dayTotal === 0) {
      setSelectedPeriod('week');   // ⬅️ défaut sur Semaine
    }
  }, [selectedPeriod, tasksRaw, todayPending]);



  // sous-ensemble de logs pour la période KPI
  const [kpiStart, kpiEnd] = useMemo(() => getKpiBounds(selectedPeriod), [selectedPeriod]);
  const logsForKpi = useMemo(
    () => (tasksRaw || []).filter(t => t.createdAt && t.createdAt >= kpiStart && t.createdAt <= kpiEnd),
    [tasksRaw, kpiStart, kpiEnd]
  );

  // compte par statut (sur logs)
  const { done: doneCnt, incident: incCnt, missed: msCnt, failed: flCnt } = useMemo(
    () => countByStatus(logsForKpi),
    [logsForKpi]
  );

  // opérateurs actifs = ceux qui ont AU MOINS 1 "done" sur la période
  const activeOperators = useMemo(() => {
    const s = new Set();
    for (const t of logsForKpi) {
      if (t.statusType === 'done' && t.operator && t.operator !== 'Non assigné') {
        s.add(t.operator);
      }
    }
    return s.size;
  }, [logsForKpi]);

  // ✅ Règles demandées :
  // Réalisées = done + incident
  // Dénominateur = done + incident + failed + missed  (on exclut 'pending')
  const realizedCnt = useMemo(() => doneCnt + incCnt, [doneCnt, incCnt]);
  const kpiDenominator = useMemo(
    () => Math.max(0, doneCnt + incCnt + flCnt + msCnt),
    [doneCnt, incCnt, flCnt, msCnt]
  );
  const completionRate = kpiDenominator ? Math.round((realizedCnt / kpiDenominator) * 100) : 0;
  const incidentRate   = kpiDenominator ? Math.round(((incCnt + flCnt + msCnt) / kpiDenominator) * 100) : 0;

  const kpiTotalTasks = useMemo(() => {
    if (selectedPeriod === 'day') {
      // On conserve ta logique existante pour l'affichage du "Nombre de tâches".
      const fallback = (todayPending?.length || 0) + doneCnt + incCnt + flCnt + msCnt;
      return (instancesCount ?? fallback);
    }
    // Semaine/mois : on peut afficher le nombre de tâches réalisées
    return realizedCnt;
  }, [selectedPeriod, instancesCount, todayPending, doneCnt, incCnt, flCnt, msCnt, realizedCnt]);

  const kpis = useMemo(() => ({
    activeOperators,
    totalTasks: kpiTotalTasks,
    completionRate,
    incidentRate
  }), [activeOperators, kpiTotalTasks, completionRate, incidentRate]);

  // Tableau
  const [tableStart, tableEnd] = useMemo(
    () => getTableBounds(selectedPeriod),
    [selectedPeriod, nowTick] // nowTick pour faire glisser la borne "aujourd'hui" sans recharger
  );
  const tasksForTable = useMemo(() =>
    (tasksRaw || []).filter(t => t.createdAt && t.createdAt >= tableStart && t.createdAt <= tableEnd),
    [tasksRaw, tableStart, tableEnd]
  );

  const operators = useMemo(() => {
    const m = new Map();
    logsForKpi.forEach(t => {
      // Les tâches réalisées incluent done + incident
      if (t.statusType === 'done' || t.statusType === 'incident') {
        m.set(t.operator, (m.get(t.operator) || 0) + 1);
      }
    });
    return Array.from(m.entries())
      .map(([name, tasksCompleted], i) => ({
        id: name || `op-${i}`,
        name: name || 'Non assigné',
        avatar: '👤',
        tasksCompleted,
      }))
      .sort((a, b) => b.tasksCompleted - a.tasksCompleted);
  }, [logsForKpi]);

  // Normalise les task_instance du jour pour la même table
  const todayRowsUI = useMemo(() => {
    return (todayPending || []).map(r => {
      const { h, m } = parseTime(r.start_time);
      const hhmm = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
      return {
        id: `ti-${r.id}`,
        title: r.title,
        operator: displayOperator(r),
        dateStr: hhmm,                 // on met l'heure dans la colonne Date/Heure
        statusType: 'ongoing',         // couleur bleue
        status: 'Pas commencé',
      };
    });
  }, [todayPending]);

  // L'ordre final : instances du jour d'abord, puis l'historique (logs)
  const allRows = useMemo(() => {
    // Pour 'Jour' on met d'abord les instances du jour (todayPending), puis les logs de la semaine en cours
    if (selectedPeriod === 'day') return [...todayRowsUI, ...tasksForTable];
    // Pour 'Semaine dernière' et 'Mois' on n'affiche que les logs filtrés
    return tasksForTable;
  }, [todayRowsUI, tasksForTable, selectedPeriod]);

  // Agrégation du graphe (indépendant du sélecteur du header, **toujours** semaine en cours)
  const CHART_H = 180;

  const chartBuckets = useMemo(() => {
    const [start, end] = thisWeekBounds();
    const buckets = Array.from({ length: 7 }, (_, i) => ({
      completed: 0, orange: 0, red: 0, total: 0,
      label: MON_FIRST[i].substring(0, 3)   // ← Lun, Mar, …, Dim
    }));

    (Array.isArray(tasksRaw) ? tasksRaw : []).forEach(t => {
      if (!t.createdAt || t.createdAt < start || t.createdAt > end) return;
      const i = mondayIndex(t.createdAt);
      if (t.statusType === 'done') {
        buckets[i].completed += 1;
        buckets[i].total += 1;
      } else if (t.statusType === 'incident' || t.statusType === 'failed') {
        // ⚠️ "failed" compte avec les incidents (barre orange)
        buckets[i].orange += 1;
        buckets[i].total += 1;
      } else if (t.statusType === 'missed') {
        buckets[i].red += 1;
        buckets[i].total += 1;
      }
    });

    return buckets;
  }, [tasksRaw]);

  // Échelle du graphe : utiliser le max réel (minimum 6)
  const chartMax = useMemo(() => Math.max(6, ...chartBuckets.map(b => b.total)), [chartBuckets]);

  // (optionnel debug)
  // console.log(tasksForKpi.map(t => t.statusType), incidentsCount, tasksForKpi.length);
  // console.log('KPI check', { total: tasksForKpi.length, incidentsCount, byStatus: tasksForKpi.reduce((a,t)=> (a[t.statusType]=(a[t.statusType]||0)+1,a),{}) });

  // Fonction pour gérer le changement de période
  const handlePeriodChange = (newPeriod) => {
    userPickedPeriodRef.current = true;
    setSelectedPeriod(newPeriod);
  };

  const getStatusIcon = (statusType) => {
    switch (statusType) {
      case 'ongoing':  return '⏳';
      case 'done':     return '✅';
      case 'incident': return '⚠';   // symbole qui hérite bien de la couleur
      case 'missed':   return '✕';   // hérite de la couleur (rouge)
      case 'failed':   return '✕';   // hérite de la couleur (gris)
      case 'pending':  return '•';
      default:         return '📋';
    }
  };

  // Dot circulaire (même style que "incident") pour tous les statuts
  const getDotClass = (statusType) => {
    switch (statusType) {
      case 'incident': return 'dot-orange';
      case 'done':     return 'dot-green';
      case 'missed':   return 'dot-red';
      case 'failed':   return 'dot-grey';
      case 'ongoing':  return 'dot-blue';
      default:         return 'dot-grey';
    }
  };

  const getStatusClass = (statusType) => {
    switch (statusType) {
      case 'ongoing':
        return 'status-ongoing';
      case 'done':
        return 'status-completed';
      case 'incident': return 'status-incident';  // 🟠
      case 'missed':   return 'status-not-done';  // 🔴
      case 'failed':   return 'status-failed';    // ⚪️/gris
      case 'pending':
        return 'status-pending';
      default:
        return 'status-default';
    }
  };

  const totalTasks = kpis.totalTasks;
  const completedTasks = realizedCnt;

  return (
    <div className="tasks-container">
      {/* Barre latérale */}
      <div className="sidebar">
        <div className="logo">CabConnect</div>
        <nav className="nav-menu">
          <div className="nav-item active" onClick={() => onNavigate && onNavigate('tasks')}>
            <span className="nav-icon">📁</span>
            <span>Taches</span>
          </div>
          <div
            className="nav-item"
            role="button"
            tabIndex={0}
            onClick={() => onNavigate && onNavigate('sterilization')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigate && onNavigate('sterilization'); }}
          >
            <span className="nav-icon">📦</span>
            <span>Stérilisation</span>
          </div>
          <div
            className="nav-item"
            role="button"
            tabIndex={0}
            onClick={() => onNavigate && onNavigate('operators')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigate && onNavigate('operators'); }}
          >
            <span className="nav-icon">👤</span>
            <span>Opérateurs</span>
          </div>
          <div
            className="nav-item"
            role="button"
            tabIndex={0}
            onClick={() => onNavigate && onNavigate('activity')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigate && onNavigate('activity'); }}
          >
            <span className="nav-icon">📊</span>
            <span>Activité</span>
          </div>
          <div
            className="nav-item"
            role="button"
            tabIndex={0}
            onClick={() => onNavigate && onNavigate('agenda')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigate && onNavigate('agenda'); }}
          >
            <span className="nav-icon">🗓️</span>
            <span>Agenda</span>
          </div>
          <div
            className="nav-item"
            role="button"
            tabIndex={0}
            onClick={() => onNavigate && onNavigate('orders')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigate && onNavigate('orders'); }}
          >
            <span className="nav-icon">🛒</span>
            <span>Commandes</span>
          </div>
        </nav>
      </div>

      {/* Contenu principal */}
      <div className="main-content">
        {/* En-tête */}
        <div className="header">
          <div className="header-left">
            <h1>Tache du cabinet</h1>
            <select className="period-filter" value={selectedPeriod} onChange={(e) => handlePeriodChange(e.target.value)}>
              <option value="day">Jour</option>
              <option value="week">Semaine dernière</option>
              <option value="month">Mois</option>
              {/* 'year' n'est pas préchargé côté backend pour les opérateurs, on le retire pour éviter toute confusion */}
            </select>
          </div>
          <div className="header-right">
            <button className="new-task-btn">
              <span>+</span>
              Nouvelle tâche
            </button>
            <div className="quick-actions">
              <div className="action-item">
                <span className="action-icon">📁</span>
                <span>Modèle de tâches</span>
              </div>
            </div>
          </div>
        </div>

        {/* Indicateurs clés */}
        <div className="key-indicators">
          <div className="indicator-card">
            <h3>Opérateur actifs</h3>
            <div className="indicator-value">{kpis.activeOperators}</div>
          </div>
          <div className="indicator-card">
            <h3>{selectedPeriod === 'day' ? 'Nombre de taches' : 'Tâches réalisées'}</h3>
            <div className="indicator-value">{kpis.totalTasks}</div>
          </div>
          <div className="indicator-card">
            <h3>Pourcentage réalisé</h3>
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill blue" 
                  style={{ width: `${kpis.completionRate}%` }}
                ></div>
              </div>
              <span className="progress-text">{kpis.completionRate} %</span>
            </div>
          </div>
          <div className="indicator-card">
            <h3>Incidents</h3>
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill red" 
                  style={{ width: `${kpis.incidentRate}%` }}
                ></div>
              </div>
              <span className="progress-text red">{kpis.incidentRate} %</span>
            </div>
          </div>
        </div>

        {/* Contenu principal avec tableau et graphique */}
        <div className="content-grid">
          {/* Liste des tâches */}
          <div className="tasks-list">
            <h2>Liste des tâches</h2>
            <div className="tasks-table">
              <div className="table-header">
                <div className="header-cell">Titre de la tache</div>
                <div className="header-cell">Opérateur</div>
                <div className="header-cell">Date/Heure</div>
                <div className="header-cell">Statut</div>
              </div>
              <div className="table-body">
                {allRows.map(row => (
                  <div key={row.id} className="table-row">
                    <div className="table-cell cell-title">{row.title}</div>
                    <div className="table-cell cell-operator">{row.operator}</div>
                    <div className="table-cell cell-date">{row.dateStr}</div>
                    <div className={`table-cell cell-status ${getStatusClass(row.statusType)}`}>
                      <span className={`status-dot ${getDotClass(row.statusType)}`} />
                      <span className="status-icon">{getStatusIcon(row.statusType)}</span>
                      <span className="status-label">{row.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Bouton "Charger plus" */}
            {(() => {
              const c = (typeof window !== 'undefined') ? (cacheGet(KEY_CURSOR) || {}) : {};
              if (!c.next) return null;
              return (
                <div style={{ textAlign: 'center', margin: '16px 0' }}>
                  <button className="btn btn-light" onClick={() => window.__tasksLoadMore?.()}>
                    Charger plus…
                  </button>
                </div>
              );
            })()}
          </div>

          {/* Graphique et opérateurs */}
          <div className="right-panel">
            {/* Graphique statistique */}
            <div className="stats-chart">
              <h3>Nombre de tâche réalisé par jour</h3>

              <div className="chart-container taller">
                <div className="chart-y-axis">
                  {[
                    <span key="max">{chartMax}</span>,
                    <span key="mid">{Math.ceil(chartMax * 2/3)}</span>,
                    <span key="third">{Math.ceil(chartMax / 3)}</span>,
                    <span key="zero">0</span>
                  ]}
                </div>

                <div className="chart-bars" ref={chartRef} style={{ height: `${CHART_H + 10}px` }}>
                  {chartBuckets.map((b, idx) => {
                    const hBlue   = (b.completed / chartMax) * CHART_H;
                    const hOrange = (b.orange    / chartMax) * CHART_H;
                    const hRed    = (b.red       / chartMax) * CHART_H;

                    const segments = [];
                    if (b.total === 0) {
                      // petite base bleue quand il n'y a aucune tâche ce jour-là
                      segments.push({ cls: 'completed', h: 4, title: '0 tâches' });
                    } else {
                      if (hRed    > 0) segments.push({ cls:'not-done',  h:hRed,    title:`${b.red} non réalisées` });
                      if (hOrange > 0) segments.push({ cls:'late',      h:hOrange, title:`${b.orange} incidents / non réalisables` });
                      if (hBlue   > 0) segments.push({ cls:'completed', h:hBlue,   title:`${b.completed} réalisées` });
                    }

                    return (
                      <div
                        key={idx}
                        className="chart-bar-group"
                        onMouseEnter={(e)=>showTT(e,b)}
                        onMouseMove={moveTT}
                        onMouseLeave={hideTT}
                      >
                        <div className="chart-bar">
                          {segments.map((s,i) => (
                            <div key={i} className={`bar-segment ${s.cls}`} style={{ height: `${s.h}px` }} />
                          ))}
                        </div>
                        <span className="bar-label">{b.label}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Tooltip */}
                {tt.show && tt.bucket && (
                  <div
                    className="chart-tooltip floating"
                    style={{ left: tt.x, top: tt.y }}
                  >
                    {tt.bucket.completed > 0 && (
                      <div className="tt-row"><span className="dot blue" /> {tt.bucket.completed} tâches réalisées</div>
                    )}
                    {tt.bucket.orange > 0 && (
                      <div className="tt-row"><span className="dot orange" /> {tt.bucket.orange} incidents / non réalisables</div>
                    )}
                    {tt.bucket.red > 0 && (
                      <div className="tt-row"><span className="dot red" /> {tt.bucket.red} non réalisées</div>
                    )}
                  </div>
                )}
              </div>

              <div className="chart-legend">
                <div className="legend-item"><span className="legend-color red"></span><span>Tâches non réalisées</span></div>
                <div className="legend-item"><span className="legend-color orange"></span><span>Incidents (incl. non réalisables)</span></div>
                <div className="legend-item"><span className="legend-color blue"></span><span>Tâches réalisées</span></div>
              </div>
            </div>

            {/* Suivi des opérateurs */}
            <div className="operators-section">
              <div className="section-header">
                <h3>Operateurs</h3>
                                <select
                  className="period-selector"
                  value={selectedPeriod}
                  onChange={(e) => handlePeriodChange(e.target.value)}
                >
                  <option value="day">Aujourd'hui</option>
                  <option value="week">Semaine dernière</option>
                  <option value="month">Ce mois</option>
                </select>
              </div>
              <div className="operators-list">
                {operators.length > 0 ? (
                  operators.map((operator, index) => (
                    <div key={operator.id} className="operator-card">
                      <div className="operator-rank">#{index + 1}</div>
                      <div className="operator-info">
                        <div className="operator-name">{operator.name}</div>
                        <div className="operator-stats">
                          <span className="tasks-count">{operator.tasksCompleted}</span>
                          <span className="tasks-label">tâches réalisées</span>
                        </div>

                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-operators">
                    <p>Aucune tâche réalisée pour cette période</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tasks;